import { useInviteToken } from '@/components/auth/useInviteToken';
import { formatDate } from '@/helpers/dates';
import { preBillingAdjustmentTranslations } from '@/helpers/preBillingAdjustmentTranslations';
import { useTranslation } from '@/hooks/useTranslation';
import {
  useGetRepricedQuoteOptionsQuery,
  useAcceptPreBillingAdjustmentMutation,
} from '@/services/preBillingAdjustmentOffer';
import { PalmettoButton, PalmettoIcon } from '@palmetto/dsm-components';
import { Themes } from '@palmetto/dsm-tokens';
import { Spinner } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

const INACTIVE_STATUSES = new Set(['voided', 'accepted']);

export const PreBillingAdjustmentOffer = () => {
  const { id } = useParams<{ id: string }>();
  const { inviteToken } = useInviteToken();
  const { translate } = useTranslation({ useAccountPreference: true });
  const t = (key: string) => translate(key, preBillingAdjustmentTranslations);
  const [acceptOffer, { isLoading: isAccepting, isError: isAcceptError, isSuccess: isAccepted }] =
    useAcceptPreBillingAdjustmentMutation();

  const { data, isLoading, isError } = useGetRepricedQuoteOptionsQuery(
    { accountId: id!, inviteToken },
    { skip: !id },
  );

  const isInactive = data ? INACTIVE_STATUSES.has(data.offerStatus) : false;
  const customerName = data?.signers[0]?.name ?? '';
  const firstName = customerName.split(' ')[0] ?? '';
  const currentSignerEmail = data?.signers[0]?.email ?? '';
  const currentSignerHasAccepted = data?.signers[0]?.hasAccepted ?? false;
  const otherSigners = data?.signers.slice(1) ?? [];
  const address = data?.address;
  const formattedAddress = address
    ? [address.address1, address.address2, `${address.city}, ${address.state} ${address.zip}`]
        .filter(Boolean)
        .join(', ')
    : null;

  if (data?.offerStatus === 'expired') {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <div className="flex justify-center pt-6">
            <img
              src="/palmetto-brand-logo.svg"
              alt="Palmetto LightReach"
              style={{ height: '44px', width: 'auto' }}
            />
          </div>
          <div className="flex flex-1 items-center justify-center px-8">
            <h1 className="text-center heading-700 text-text-primary">{t('expired')}</h1>
          </div>
        </div>
      </div>
    );
  }

  if ((isAccepted || currentSignerHasAccepted) && data && !isInactive) {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <div className="flex justify-center pt-6">
            <img
              src="/palmetto-brand-logo.svg"
              alt="Palmetto LightReach"
              style={{ height: '44px', width: 'auto' }}
            />
          </div>

          <div className="flex flex-1 items-center justify-center px-8">
            <div className="flex flex-col gap-8 text-center" style={{ maxWidth: '44rem' }}>
              <div className="flex items-center justify-center gap-12">
                <PalmettoIcon name="celebrate" size="4xl" />
                <h1 className="heading-800 text-text-primary">
                  {t('confirmationHeading')}
                  {firstName ? `, ${firstName}` : ''}!
                </h1>
              </div>
              <p className="body-600 text-text-primary">
                {otherSigners.length > 0 ? (
                  <>
                    {t('confirmationBodyAdditionalSignersBefore')}{' '}
                    {otherSigners.map((signer, i) => (
                      <span key={signer.email}>
                        <strong>{signer.name}</strong>
                        {i < otherSigners.length - 1 ? ', ' : ' '}
                      </span>
                    ))}
                    {t('confirmationBodyAdditionalSignersAfter')}
                  </>
                ) : (
                  <>
                    {t('confirmationBodySolo')} <strong>{currentSignerEmail}</strong>.
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 pb-12 text-center body-400 text-text-tertiary">
            <p>{t('confirmationQuestion')}</p>
            <p>
              {t('confirmationContactBefore')} <strong>{t('contactEmail')}</strong> {t('confirmationOr')}{' '}
              <strong>{t('contactPhone')}</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-theme={Themes.core}>
      <Helmet>
        <title>{t('helmetTitle')}</title>
      </Helmet>

      {isLoading && (
        <div className="flex min-h-screen items-center justify-center bg-canvas-default">
          <Spinner />
        </div>
      )}

      {(isError || (!isLoading && !data)) && (
        <div className="flex min-h-screen items-center justify-center bg-canvas-default">
          <p className="body-300 text-text-primary">{t('errorLoading')}</p>
        </div>
      )}

      {data && (
        <>
          <div className="flex min-h-screen flex-col bg-canvas-default" style={{ paddingBottom: '150px' }}>
            <div className="gap-7 flex flex-col items-center px-4 pt-6 pb-12">
              <img
                className="mb-24"
                src="/palmetto-brand-logo.svg"
                alt="Palmetto LightReach"
                style={{ height: '50px', width: 'auto' }}
              />

              <div className="gap-7 flex w-full max-w-2xl flex-col items-center text-center">
                <h1 className="mb-20 heading-700 text-text-primary">
                  {t('heading')}
                  {firstName ? `, ${firstName}` : ''}.
                </h1>
              </div>
              <div
                className="w-full body-stylized-500 text-text-primary"
                style={{ maxWidth: '64rem', display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '2rem' }}
              >
                <span className="whitespace-nowrap">{customerName}</span>
                <span className="whitespace-nowrap">{id}</span>
                <span className="whitespace-nowrap" style={{ textAlign: 'right' }}>
                  {formattedAddress}
                </span>
              </div>
            </div>

            <div className="mt-24 flex justify-center border-0 p-4">
              <div
                className="w-full overflow-hidden rounded-36 border-2 border-canvas-inverted"
                style={{ maxWidth: '64rem' }}
              >
                <div className="flex items-center justify-center rounded-t-36 bg-canvas-inverted px-4 py-4">
                  <span className="heading-200 text-text-on-inverted-primary">{t('fullContract')}</span>
                </div>
                {data.contractHtml ? (
                  <iframe
                    srcDoc={data.contractHtml}
                    title="Full contract"
                    onLoad={(e) => {
                      const iframe = e.currentTarget;
                      if (iframe.contentDocument?.documentElement) {
                        iframe.style.height = `${iframe.contentDocument.documentElement.scrollHeight + 50}px`;
                      }
                    }}
                    style={{ width: '100%', height: 0, border: 'none', overflow: 'hidden' }}
                  />
                ) : (
                  <div className="min-h-96 bg-canvas-default p-8" />
                )}
              </div>
            </div>
          </div>

          <div className="fixed z-50 bg-canvas-bolder" style={{ bottom: 0, left: 0, right: 0 }}>
            <div className="mx-auto flex max-w-5xl justify-end px-8 py-10">
              <div className="flex flex-col items-center gap-2">
                {!isInactive && <p className="text-center body-200 text-text-secondary">{t('acceptDisclaimer')}</p>}
                {!isInactive && (
                  <PalmettoButton
                    emphasis="primary"
                    size="large"
                    layout="hug"
                    trailingIcon={<PalmettoIcon name="arrowRight" size="sm" />}
                    loading={isAccepting}
                    disabled={isAccepting}
                    onClick={() => acceptOffer({ accountId: id!, acceptedName: customerName, inviteToken })}
                  >
                    {t('accept')}
                  </PalmettoButton>
                )}
                {data.expiresAt && !isInactive && (
                  <p className="text-center body-200 text-text-secondary">
                    {t('contractExpires')} {formatDate(data.expiresAt, DateTime.DATE_FULL)}
                  </p>
                )}
                {isInactive && (
                  <p className="text-center body-300 text-text-primary">
                    {data.offerStatus === 'accepted' ? t('accepted') : t('voided')}
                  </p>
                )}
                {isAcceptError && <p className="text-center body-200 text-text-error">{t('acceptError')}</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
